import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type {
  Equipment,
  EquipmentCriticality,
  EquipmentPayload,
  EquipmentStatus,
  CalibrationStatus,
} from '../features/maintenance/types';
import { RequiredFieldsLegend } from './RequiredFieldsLegend';

const schema = z.object({
  code: z.string().min(1, 'Requerido').max(64),
  name: z.string().min(1, 'Requerido').max(255),
  type: z.string().trim().max(120).optional(),
  location: z.string().trim().max(255).optional(),
  brand: z.string().trim().max(120).optional(),
  model: z.string().trim().max(120).optional(),
  serialNumber: z.string().trim().max(120).optional(),
  criticality: z.enum(['alta', 'media', 'baja']),
  status: z.enum(['operativo', 'fuera_de_servicio', 'en_reparacion', 'baja']),
  isInstrument: z.boolean(),
  measurementRange: z.string().trim().max(255).optional(),
  uncertainty: z.string().trim().max(255).optional(),
  calibrationFrequencyMonths: z.coerce.number().int().min(1).max(120).optional(),
  calibrationStandard: z.string().trim().max(255).optional(),
  tolerance: z.string().trim().max(120).optional(),
  nextCalibrationAt: z.string().optional(),
  calibrationStatus: z
    .union([z.enum(['vigente', 'por_vencer', 'vencido', 'fuera_de_uso']), z.literal('')])
    .optional()
    .transform((v) => (v === '' || v === undefined ? undefined : v)),
  notes: z.string().trim().max(5000).optional(),
});

type FormValues = z.infer<typeof schema>;

function emptyToNull(v: string | undefined | null): string | null {
  return v && v.length > 0 ? v : null;
}

const CRIT: { value: EquipmentCriticality; label: string }[] = [
  { value: 'alta', label: 'Alta' },
  { value: 'media', label: 'Media' },
  { value: 'baja', label: 'Baja' },
];

const STATUSES: { value: EquipmentStatus; label: string }[] = [
  { value: 'operativo', label: 'Operativo' },
  { value: 'fuera_de_servicio', label: 'Fuera de servicio' },
  { value: 'en_reparacion', label: 'En reparación' },
  { value: 'baja', label: 'Baja' },
];

const CALIBRATION_STATUS: { value: CalibrationStatus; label: string }[] = [
  { value: 'vigente', label: 'Vigente' },
  { value: 'por_vencer', label: 'Por vencer' },
  { value: 'vencido', label: 'Vencido' },
  { value: 'fuera_de_uso', label: 'Fuera de uso' },
];

interface Props {
  initial?: Equipment | null;
  loading?: boolean;
  onSubmit: (payload: EquipmentPayload) => void;
  onCancel: () => void;
  readOnly?: boolean;
}

export function EquipmentForm({ initial, loading, onSubmit, onCancel, readOnly }: Props) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: toFormValues(initial),
  });

  useEffect(() => {
    reset(toFormValues(initial));
  }, [initial, reset]);

  const isInstrument = watch('isInstrument');

  const submit = handleSubmit((values) => {
    onSubmit({
      code: values.code,
      name: values.name,
      type: emptyToNull(values.type),
      location: emptyToNull(values.location),
      brand: emptyToNull(values.brand),
      model: emptyToNull(values.model),
      serialNumber: emptyToNull(values.serialNumber),
      criticality: values.criticality,
      status: values.status,
      isInstrument: values.isInstrument,
      measurementRange: emptyToNull(values.measurementRange),
      uncertainty: emptyToNull(values.uncertainty),
      calibrationFrequencyMonths: values.calibrationFrequencyMonths ?? null,
      calibrationStandard: emptyToNull(values.calibrationStandard),
      tolerance: emptyToNull(values.tolerance),
      nextCalibrationAt: emptyToNull(values.nextCalibrationAt),
      calibrationStatus: values.calibrationStatus || undefined,
      notes: emptyToNull(values.notes),
    });
  });

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Código *" error={errors.code?.message}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Ej: EQ-001"
          {...register('code')}
        />
      </Field>
      <Field label="Nombre *" error={errors.name?.message}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Nombre del equipo"
          {...register('name')}
        />
      </Field>
      <Field label="Tipo" error={errors.type?.message}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Ej: Bomba, torno…"
          {...register('type')}
        />
      </Field>
      <Field label="Ubicación" error={errors.location?.message}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Ej: Planta 1 — línea A"
          {...register('location')}
        />
      </Field>
      <Field label="Marca" error={errors.brand?.message}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Marca"
          {...register('brand')}
        />
      </Field>
      <Field label="Modelo" error={errors.model?.message}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Modelo"
          {...register('model')}
        />
      </Field>
      <Field label="N° de serie" error={errors.serialNumber?.message}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Número de serie"
          {...register('serialNumber')}
        />
      </Field>
      <Field label="Criticidad" error={errors.criticality?.message}>
        <select className="border rounded-md px-3 py-2 w-full" disabled={readOnly} {...register('criticality')}>
          {CRIT.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Estado" error={errors.status?.message}>
        <select className="border rounded-md px-3 py-2 w-full" disabled={readOnly} {...register('status')}>
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Es instrumento (sujeto a calibración)" colSpan={2}>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" disabled={readOnly} {...register('isInstrument')} />
          <span className="text-sm text-muted-foreground">
            Marcar si requiere control metrológico
          </span>
        </label>
      </Field>
      {isInstrument && (
        <>
          <Field label="Rango de medición" error={errors.measurementRange?.message}>
            <input
              className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
              disabled={readOnly}
              placeholder="Ej: 0–100 °C"
              {...register('measurementRange')}
            />
          </Field>
          <Field label="Incertidumbre" error={errors.uncertainty?.message}>
            <input
              className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
              disabled={readOnly}
              placeholder="Ej: ±0,5 °C"
              {...register('uncertainty')}
            />
          </Field>
          <Field label="Frecuencia de calibración (meses)" error={errors.calibrationFrequencyMonths?.message}>
            <input
              type="number"
              min={1}
              max={120}
              className="border rounded-md px-3 py-2 w-full"
              disabled={readOnly}
              placeholder="Ej: 12"
              {...register('calibrationFrequencyMonths')}
            />
          </Field>
          <Field label="Patrón / estándar de referencia" error={errors.calibrationStandard?.message}>
            <input
              className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
              disabled={readOnly}
              placeholder="Ej: Patrón nacional de temperatura"
              {...register('calibrationStandard')}
            />
          </Field>
          <Field label="Tolerancia admisible" error={errors.tolerance?.message}>
            <input
              className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
              disabled={readOnly}
              placeholder="Ej: ±1 %"
              {...register('tolerance')}
            />
          </Field>
          <Field label="Próxima calibración" error={errors.nextCalibrationAt?.message}>
            <input
              type="date"
              className="border rounded-md px-3 py-2 w-full"
              disabled={readOnly}
              {...register('nextCalibrationAt')}
            />
          </Field>
          <Field label="Estado metrológico" error={errors.calibrationStatus?.message}>
            <select className="border rounded-md px-3 py-2 w-full" disabled={readOnly} {...register('calibrationStatus')}>
              <option value="">Automático por fecha</option>
              {CALIBRATION_STATUS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </Field>
        </>
      )}
      <Field label="Notas" error={errors.notes?.message} colSpan={2}>
        <textarea
          rows={3}
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Observaciones"
          {...register('notes')}
        />
      </Field>

      <RequiredFieldsLegend className="md:col-span-2" />
      <div className="md:col-span-2 flex justify-end gap-2 pt-2">
        <button
          type="button"
          className="rounded-md px-4 py-2 text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80"
          onClick={onCancel}
        >
          Cancelar
        </button>
        {!readOnly && (
          <button
            type="submit"
            disabled={loading}
            className="rounded-md px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? 'Guardando…' : initial ? 'Guardar' : 'Crear'}
          </button>
        )}
      </div>
    </form>
  );
}

function toFormValues(initial?: Equipment | null): FormValues {
  return {
    code: initial?.code ?? '',
    name: initial?.name ?? '',
    type: initial?.type ?? '',
    location: initial?.location ?? '',
    brand: initial?.brand ?? '',
    model: initial?.model ?? '',
    serialNumber: initial?.serialNumber ?? '',
    criticality: initial?.criticality ?? 'media',
    status: initial?.status ?? 'operativo',
    isInstrument: initial?.isInstrument ?? false,
    measurementRange: initial?.measurementRange ?? '',
    uncertainty: initial?.uncertainty ?? '',
    calibrationFrequencyMonths: initial?.calibrationFrequencyMonths ?? undefined,
    calibrationStandard: initial?.calibrationStandard ?? '',
    tolerance: initial?.tolerance ?? '',
    nextCalibrationAt: initial?.nextCalibrationAt ? initial.nextCalibrationAt.slice(0, 10) : '',
    calibrationStatus: initial?.calibrationStatus,
    notes: initial?.notes ?? '',
  };
}

function Field({
  label,
  children,
  error,
  colSpan,
}: {
  label: string;
  children: React.ReactNode;
  error?: string;
  colSpan?: 1 | 2;
}) {
  return (
    <div className={colSpan === 2 ? 'md:col-span-2' : ''}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}
